import React, { useState, useEffect, useMemo } from 'react';
import AuthService from '../services/authService';
import Spinner from './Spinner';

interface UpdateUserFormProps {
  userId?: number;
  initialUserData?: UserData;
  onUpdateSuccess: () => void;
  onCancel: () => void;
}

interface UserData {
  id: number;
  username: string;
  email: string;
  full_name: string;
  designation: string;
  team_id: number;
  team_code: number;
  team_name?: string;
}

interface Team {
  id: number;
  team_code: number;
  team_name: string;
}

interface FormErrors {
  username?: string;
  email?: string;
  fullName?: string;
  password?: string;
  confirmPassword?: string;
  teamId?: string;
  designation?: string;
  general?: string;
}

const UpdateUserForm: React.FC<UpdateUserFormProps> = ({ userId: initialUserId, initialUserData, onUpdateSuccess, onCancel }) => {
  const [userData, setUserData] = useState<UserData | null>(initialUserData || null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [userNameQuery, setUserNameQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(initialUserId);
  const [showUserForm, setShowUserForm] = useState(!!initialUserId || !!initialUserData);
  const [username, setUsername] = useState(initialUserData?.username || '');
  const [email, setEmail] = useState(initialUserData?.email || '');
  const [fullName, setFullName] = useState(initialUserData?.full_name || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [teamId, setTeamId] = useState<number | ''>(initialUserData?.team_id || '');
  const [designation, setDesignation] = useState(initialUserData?.designation || '');
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamNameQuery, setTeamNameQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(!!initialUserId && !initialUserData);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const authService = new AuthService();

  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await authService.getAllTeams();
        if (response.status === 200) {
          setTeams(response.data);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams();
  }, []);

  // Set initial selected team when teams are loaded and we have initialUserData
  useEffect(() => {
    if (teams.length > 0 && initialUserData?.team_id && !selectedTeam) {
      const userTeam = teams.find(team => team.id === initialUserData.team_id);
      if (userTeam) {
        setSelectedTeam(userTeam);
      }
    }
  }, [teams, initialUserData, selectedTeam]);

  // Fetch user data when a user is selected (only for initial userId prop without initialUserData)
  useEffect(() => {
    const fetchUserData = async () => {
      if (!selectedUserId || initialUserData) {
        setIsLoadingUser(false);
        return;
      }
      
      // If we already have userData for this user ID, don't fetch again
      if (userData && userData.id === selectedUserId) {
        setIsLoadingUser(false);
        return;
      }
      
      try {
        setIsLoadingUser(true);
        const response = await authService.getUserById(selectedUserId);
        if (response.status === 200) {
          const user = response.data;
          setUserData(user);
          setUsername(user.username);
          setEmail(user.email);
          setFullName(user.full_name);
          setTeamId(user.team_id);
          setDesignation(user.designation || '');
          
          // Find and set the selected team based on team_id
          if (teams.length > 0 && user.team_id) {
            const userTeam = teams.find(team => team.id === user.team_id);
            if (userTeam) {
              setSelectedTeam(userTeam);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setErrors({ general: 'Failed to load user data' });
      } finally {
        setIsLoadingUser(false);
      }
    };

    // Only fetch if this is the initial userId passed as prop and we don't have initialUserData
    if (selectedUserId === initialUserId && !initialUserData) {
      fetchUserData();
    }
  }, [selectedUserId, teams, userData, initialUserId, initialUserData]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (password && password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (password && password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!teamId) {
      newErrors.teamId = 'Team is required';
    }
    
    if (!designation.trim()) {
      newErrors.designation = 'Designation is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Debounced search for users based on query
  useEffect(() => {
    const searchUsers = async () => {
      if (userNameQuery.length < 2) {
        setUsers([]);
        return;
      }

      try {
        setIsLoadingUser(true);
        const response = await authService.searchUsers(userNameQuery);
        if (response.status === 200) {
          setUsers(response.data.users || []);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [userNameQuery]);

  // Filter users based on query
  const filteredUsers = useMemo(() => {
    if (!userNameQuery || !users || !Array.isArray(users)) return [];
    return users.filter((user) => 
      user && typeof user === 'object' && 
      ((user.username && typeof user.username === 'string' && 
        user.username.toLowerCase().includes(userNameQuery.toLowerCase())) ||
       (user.email && typeof user.email === 'string' && 
        user.email.toLowerCase().includes(userNameQuery.toLowerCase())) ||
       (user.full_name && typeof user.full_name === 'string' && 
        user.full_name.toLowerCase().includes(userNameQuery.toLowerCase())))
    );
  }, [users, userNameQuery]);
  
  // Filter teams based on query
  const filteredTeams = useMemo(() => {
    if (!teamNameQuery || !teams || !Array.isArray(teams)) return [];
    return teams.filter((team) => 
      team && typeof team === 'object' && team.team_name && 
      typeof team.team_name === 'string' && 
      team.team_name.toLowerCase().includes(teamNameQuery.toLowerCase())
    );
  }, [teams, teamNameQuery]);

const handleUserSelect = (user: UserData) => {
  setSelectedUserId(user.id);
  setUserNameQuery(''); // This should clear the search
  setUsers([]); // Clear the users list to close dropdown
  setShowUserForm(true);
  
  // Use the search result data directly to populate the form
  setUserData(user);
  setUsername(user.username);
  setEmail(user.email);
  setFullName(user.full_name);
  setTeamId(user.team_id);
  setDesignation(user.designation || '');
  
  // Find and set the selected team based on team_id
  if (teams.length > 0 && user.team_id) {
    const userTeam = teams.find(team => team.id === user.team_id);
    if (userTeam) {
      setSelectedTeam(userTeam);
    }
  }
  
  // Clear any previous errors when selecting a new user
  setErrors({});
  setSuccessMessage('');
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const payload = {
        username,
        email,
        full_name: fullName,
        team_id: teamId,
        designation,
      };
      
      if (password) {
        Object.assign(payload, { password });
      }
      
      if (!selectedUserId) {
        setErrors({ general: 'No user selected' });
        return;
      }

      // Add this function in UpdateUserForm component


      const response = await authService.updateUser(selectedUserId, payload);

      if (response.status === 200) {
        setSuccessMessage('User updated successfully');
        onUpdateSuccess();
      } else {
        setErrors({ general: 'Failed to update user' });
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Handle validation errors from the backend
      if (error.response?.status === 422 && error.response?.data?.detail) {
        // Extract validation errors from the response
        const validationErrors = error.response.data.detail;
        const formattedErrors: FormErrors = {};
        
        // Process validation errors into a format our form can use
        if (Array.isArray(validationErrors)) {
          validationErrors.forEach(err => {
            // Map backend field names to frontend field names
            const fieldMap: {[key: string]: string} = {
              'UserName': 'username',
              'Email': 'email',
              'fullName': 'fullName',
              'TeamId': 'teamId',
              'designation': 'designation',
              'password': 'password'
            };
            
            // Get the field name from the error location or use the field directly
            const fieldName = err.loc?.[1] || err.loc || '';
            const mappedField = fieldMap[fieldName] || fieldName;
            
            // Use the error message as is - ensure it's a string to prevent React errors
            formattedErrors[mappedField as keyof FormErrors] = typeof err.msg === 'string' ? err.msg : String(err);
          });
        } else if (typeof validationErrors === 'string') {
          formattedErrors.general = validationErrors;
        } else {
          formattedErrors.general = 'Validation error occurred';
        }
        
        setErrors(formattedErrors);
      } else {
        // Handle general errors
        setErrors({ 
          general: typeof error.response?.data?.detail === 'string' 
            ? error.response.data.detail 
            : 'Failed to update user' 
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Only show loading spinner when fetching user data for an existing user ID
  // and not when just searching for users
  if (isLoadingUser && selectedUserId && !userData && !initialUserData) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Update User</h2>
        <p className="text-gray-600">Search for a user by name, email or username and modify their details</p>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      {/* User search input - only show if no initialUserData provided */}
      {!initialUserData && (
        <div className="mb-6">
          <label htmlFor="userSearch" className="block text-sm font-medium text-gray-700 mb-1">
            Search User
          </label>
          <div className="relative">
            <div className="flex">
              <input
                type="text"
                id="userSearch"
                className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search by username, email, or full name"
                value={userNameQuery}
                onChange={(e) => setUserNameQuery(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => userNameQuery ? setUserNameQuery('') : null}
                className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
              >
                {userNameQuery ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </button>
            </div>
            {userNameQuery.length > 0 && (
              <div className="mt-2 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-10 absolute shadow-lg">
                {isLoadingUser && userNameQuery.length >= 2 ? (
                  <div className="p-4 flex justify-center items-center">
                    <Spinner size="sm" />
                    <span className="ml-2 text-gray-600">Searching...</span>
                  </div>
                ) : users.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <li
                        key={user.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                        onClick={() => handleUserSelect(user)}
                      >
                        <div>
                          <div className="font-medium">{user.full_name}</div>
                          <div className="text-sm text-gray-600">{user.username}</div>
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </li>
                    ))}
                  </ul>
                ) : userNameQuery.length >= 2 ? (
                  <div className="p-4 text-center text-gray-500">No users found</div>
                ) : null}
              </div>
            )}
          </div>

          {userNameQuery.length > 0 && userNameQuery.length < 2 && (
            <p className="mt-1 text-sm text-gray-500">Type at least 2 characters to search</p>
          )}
        </div>
      )}

      {/* User update form - only shown when a user is selected or initialUserData is provided */}
      {showUserForm && (
        <div className={`${!initialUserData ? 'border-t pt-6 mt-6' : ''}`}>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Edit User: {userData?.full_name || userData?.username || initialUserData?.full_name || initialUserData?.username}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  id="username"
                  type="text"
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                />
                {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  id="email"
                  type="email"
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  id="fullName"
                  type="text"
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.fullName ? 'border-red-500' : 'border-gray-300'} text-base`}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={isLoading}
                  style={{ height: '42px' }} /* Fix the height to match other inputs */
                />
                {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password (leave blank to keep current)
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
              </div>

              {password && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {showConfirmPassword ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        ) : (
                          <>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="teamId" className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                <div className="relative">
                  <div className="flex">
                    <input
                      type="text"
                      id="teamSearch"
                      className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search for a team"
                      value={teamNameQuery}
                      onChange={(e) => setTeamNameQuery(e.target.value)}
                      autoComplete="off"
                      disabled={isLoading}
                    />
                    <button 
                      type="button"
                      onClick={() => teamNameQuery ? setTeamNameQuery('') : null}
                      className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
                      disabled={isLoading}
                    >
                      {teamNameQuery ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {teamNameQuery.length > 0 && (
                    <div className="mt-2 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-10 absolute shadow-lg">
                      {!filteredTeams || filteredTeams.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500">
                          No teams found
                        </div>
                      ) : (
                        <ul className="divide-y divide-gray-200">
                          {filteredTeams.map((team) => (
                            <li 
                              key={team.id}
                              className={`p-2 cursor-pointer hover:bg-blue-50 ${selectedTeam?.id === team.id ? 'bg-blue-100' : ''}`}
                              onClick={() => {
                                setSelectedTeam(team);
                                setTeamId(team.id);
                                setTeamNameQuery('');
                              }}
                            >
                              <div className="flex items-center">
                                {selectedTeam?.id === team.id && (
                                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                                  </svg>
                                )}
                                <span>{team.team_name}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                {selectedTeam && (
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="font-medium text-gray-700">Selected Team:</p>
                    <p className="text-blue-600">{selectedTeam.team_name}</p>
                  </div>
                )}
                {errors.teamId && <p className="mt-1 text-sm text-red-500">{errors.teamId}</p>}
              </div>
              
              <div>
                <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input
                  id="designation"
                  type="text"
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.designation ? 'border-red-500' : 'border-gray-300'}`}
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  disabled={isLoading}
                  placeholder="e.g., Coach, Analyst, Admin"
                />
                {errors.designation && <p className="mt-1 text-sm text-red-500">{errors.designation}</p>}
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowUserForm(false);
                  setSelectedUserId(undefined);
                  setUserData(null);
                  onCancel();
                }}
                className="bg-gray-300 text-gray-700 py-2 px-6 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 flex items-center"
              >
                {isLoading ? (
                  <div className="flex justify-center items-center">
                    <Spinner size="sm" />
                    <span className="ml-2">Updating user...</span>
                  </div>
                ) : (
                  'Update User'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UpdateUserForm;